import { useOS, useVisualViewport } from '@capsicum/common-web';
import { type PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { useInputFocusFooterAvoidance, type UseInputFocusFooterAvoidanceProps } from './hooks';

type KeyboardAwareBottomWrapperProps = PropsWithChildren<{
	scrollableEntity?: UseInputFocusFooterAvoidanceProps['scrollableEntity'];
}>;

// SCROLL_END_DURATION is the duration during which the app will wait for scrolling changes to be detected before it
// interprets them as the conclusion of the scrolling action.
const SCROLL_END_DURATION = 500;

// BROWSER_ELEMENTS_BUFFER is a small buffer of pixels to avoid moving the wrapper in response to changes to browser
// layout (ex. visibility of address bar in Chrome).
const BROWSER_ELEMENTS_BUFFER = 150;

export const KeyboardAwareBottomWrapper = ({ children, scrollableEntity }: KeyboardAwareBottomWrapperProps) => {
	const { isWeb } = useOS();
	const ref = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState(0);
	const [scrollY, setScrollY] = useState(window.scrollY);
	const [windowHeight, setWindowHeight] = useState(window.innerHeight);
	const [bottomContainerHeight, setBottomContainerHeight] = useState(0);
	const [isLayoutViewportScrolling, setLayoutViewportScrolling] = useState(false);
	const [isVisualViewportScrolling, setVisualViewportScrolling] = useState(false);
	const { height: visualViewportHeight, offsetTop: visualViewportOffsetTop } = useVisualViewport();

	useInputFocusFooterAvoidance({
		footerHeight: bottomContainerHeight,
		scrollableEntity
	});

	const isResponsive = !isWeb && visualViewportHeight + BROWSER_ELEMENTS_BUFFER < windowHeight;
	const isHidden = isResponsive && (isVisualViewportScrolling || isLayoutViewportScrolling);

	useLayoutEffect(() => {
		const { current: element } = ref;

		if (element) setBottomContainerHeight(element.offsetHeight);
	}, []);

	useEffect(() => {
		if (!isResponsive) return;

		setLayoutViewportScrolling(true);

		const timeoutID = window.setTimeout(() => setLayoutViewportScrolling(false), SCROLL_END_DURATION);

		return () => {
			window.clearTimeout(timeoutID);
		};
	}, [isResponsive, scrollY]);

	useEffect(() => {
		if (!isResponsive) return;

		setVisualViewportScrolling(true);

		const timeoutID = window.setTimeout(() => setVisualViewportScrolling(false), SCROLL_END_DURATION);

		return () => {
			window.clearTimeout(timeoutID);
		};
	}, [isResponsive, visualViewportOffsetTop]);

	useEffect(() => {
		if (!isResponsive) return;

		const listener = () => {
			setScrollY(window.scrollY);
		};

		window.addEventListener('scroll', listener);

		return () => {
			window.removeEventListener('scroll', listener);
		};
	}, [isResponsive]);

	useEffect(() => {
		const listener = () => {
			setWindowHeight(window.innerHeight);
		};

		window.addEventListener('resize', listener);

		return () => {
			window.removeEventListener('resize', listener);
		};
	}, []);

	useEffect(() => {
		setPosition(!isResponsive ? 0 : Math.max(0, windowHeight - visualViewportHeight - visualViewportOffsetTop));
	}, [isResponsive, windowHeight, visualViewportHeight, visualViewportOffsetTop]);

	return (
		<S.Wrapper $hidden={isHidden} $position={position} ref={ref}>
			{children}
		</S.Wrapper>
	);
};

namespace S {
	const ShowAnimation = keyframes`
		0% {
			opacity: 0;
			transform: translateY(100vh);
		}

		50% {
			opacity: 1;
		}

		100% {
			transform: translateY(0);
		}
	`;

	export const Wrapper = styled.div<{ $hidden: boolean; $position: number }>`
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			left: 0;
			right: 0;
			position: fixed;
			transition: transform 0.1s ease;
			bottom: ${({ $position }) => $position}px;
			opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
			${({ $hidden }) =>
				!$hidden &&
				css`
					animation: ${ShowAnimation} 0.3s linear;
				`}
		}

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			bottom: 0;
			width: 100%;
			position: sticky;
		}
	`;
}
